import { useState, useCallback, useRef } from "react";
import { api } from "../App";
import { Upload, X, Loader2, Camera, Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * Client-side image compression: downscales large dimensions and re-encodes as JPEG ~0.85.
 * Returns a data URL. Falls back to original if anything fails.
 */
async function compressImage(file, maxDim = 1800, quality = 0.85) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(1, maxDim / Math.max(width, height));
        if (scale < 1) {
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        try {
          resolve(canvas.toDataURL("image/jpeg", quality));
        } catch {
          resolve(reader.result);
        }
      };
      img.onerror = () => resolve(reader.result);
      img.src = reader.result;
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

export default function ImageUpload({
  onImageUploaded,
  imageType = "general",
  maxImages = 1,
  existingImages = [],
  className = "",
}) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [justUploaded, setJustUploaded] = useState(false);
  const [images, setImages] = useState(existingImages);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  }, []);

  const processFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Solo se permiten imágenes (JPG/PNG/WEBP)");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("La imagen no debe superar 20MB");
      return;
    }

    setUploading(true);
    setProgress(5);
    setJustUploaded(false);

    try {
      // Compress client-side so large phone photos don't blow the limit
      const dataUrl = await compressImage(file);
      setProgress(20);

      const response = await api.post(
        "/upload/image",
        { image_data: dataUrl, image_type: imageType },
        {
          onUploadProgress: (evt) => {
            if (evt.total) {
              const pct = Math.min(95, 20 + Math.round((evt.loaded / evt.total) * 75));
              setProgress(pct);
            }
          },
        }
      );

      setProgress(100);
      const imageUrl = response.data.url?.startsWith("http")
        ? response.data.url
        : `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      const newImages = [
        ...images,
        { id: response.data.image_id, url: imageUrl, data: response.data.data || dataUrl },
      ];
      while (newImages.length > maxImages) newImages.shift();

      setImages(newImages);
      onImageUploaded?.(newImages.map((img) => img.data || img.url));
      setJustUploaded(true);
      toast.success("Imagen subida exitosamente");
      setTimeout(() => setJustUploaded(false), 1800);
    } catch (e) {
      console.error("Upload error:", e);
      toast.error(e.response?.data?.detail || "Error al subir imagen");
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 600);
    }
  };

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        processFile(e.dataTransfer.files[0]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [images, maxImages]
  );

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) processFile(e.target.files[0]);
    e.target.value = "";
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    onImageUploaded?.(newImages.map((img) => img.data || img.url));
  };

  return (
    <div className={className}>
      {/* Image Previews */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          {images.map((image, index) => (
            <div
              key={index}
              className="relative aspect-square rounded-xl overflow-hidden group border border-neutral-200"
            >
              <img
                src={image.data || image.url}
                alt={`Upload ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => removeImage(index)}
                title="Eliminar"
                className="absolute top-2 right-2 w-7 h-7 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid="image-remove-btn"
              >
                <X className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                title="Reemplazar"
                className="absolute bottom-2 right-2 w-7 h-7 bg-white/90 hover:bg-white rounded-full flex items-center justify-center text-neutral-700 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid="image-retake-btn"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Hidden inputs (one for files, one for camera capture) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInput}
        className="hidden"
        disabled={uploading}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileInput}
        className="hidden"
        disabled={uploading}
      />

      {/* Dropzone */}
      {images.length < maxImages && (
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`relative dropzone ${dragActive ? "active" : ""} ${
            justUploaded ? "ring-2 ring-emerald-500" : ""
          }`}
          data-testid="image-upload-dropzone"
        >
          {/* Progress bar */}
          {uploading && (
            <div className="absolute left-0 top-0 right-0 h-1 bg-neutral-100 rounded-t-2xl overflow-hidden">
              <div
                className="h-full bg-terracotta transition-all duration-200"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}

          {/* Centered content */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="cursor-pointer flex flex-col items-center w-full focus:outline-none"
            data-testid="image-upload-trigger"
          >
            {uploading ? (
              <Loader2 className="w-10 h-10 text-neutral-400 animate-spin mb-3" />
            ) : justUploaded ? (
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-3 animate-pulse">
                <Check className="w-6 h-6 text-emerald-600" strokeWidth={3} />
              </div>
            ) : (
              <div className="w-12 h-12 bg-neutral-100 rounded-xl flex items-center justify-center mb-3">
                <Upload className="w-6 h-6 text-neutral-500" />
              </div>
            )}
            <p className="text-sm font-medium text-neutral-700 mb-1">
              {uploading
                ? `Subiendo ${progress}%`
                : justUploaded
                ? "¡Listo!"
                : "Arrastra o toca para subir"}
            </p>
            <p className="text-xs text-neutral-500">JPG · PNG · WEBP — máx. 20MB</p>
          </button>

          {/* Camera shortcut (mobile) */}
          {!uploading && (
            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-terracotta hover:text-terracotta/80 sm:hidden"
              data-testid="image-camera-btn"
            >
              <Camera className="w-3.5 h-3.5" />
              Tomar foto con la cámara
            </button>
          )}
        </div>
      )}
    </div>
  );
}
